<script lang="ts" generics="T extends string">
  /**
   * A picker's main control: one full-width `ChoiceCard` per option, stacked.
   * Each option is a whole sentence and a line or two of detail, which a pill
   * has no room for, so this is a `radiogroup` of cards rather than a
   * `SegmentedControl`. TwinTen's level cards.
   *
   * One Tab stop; the arrow keys move to the next card and choose it
   * (`radioKeys.ts`). The parent owns `value`. Generic over the option
   * values, so a string-backed union comes back from `onchange` as itself.
   *
   * Since 1.7.0:
   *
   * - an option's `lang` is set on its name, and its `avatar` draws a
   *   person's face before the name (a URL, or `null` for the initial): the
   *   picker's friends;
   * - **`deselectable`** with **`onclear`**: a second press on the chosen
   *   card clears the choice. As in `ChoiceTiles`, a deselectable group is a
   *   `group` of toggle cards (`aria-pressed`, each its own Tab stop, no
   *   arrow keys), because a radio cannot be unchosen. `value` is `null`
   *   while nothing is chosen.
   *
   * Since 1.8.0: an option's `richName` (code, a formula) is drawn in place
   * of its name, which stays what a screen reader says; an option's `tone`
   * (`right`, `wrong`) grades it once the answer is known: a check or a
   * cross, an outline in `--ui-good` or `--ui-danger`, and a word at the end
   * of its name (`toneLabels`, the shell's "Right answer" and "Wrong answer"
   * by default); an option's `detailLang` is set on the lines under its
   * name, as its `lang` is on the name.
   *
   * Test ids: `testid` on the group, each option's `testid` on its card, and
   * `chosenTaglineTestid` on the chosen card's tagline (so a test can read
   * what the choice promises). No defaults.
   */
  import { handleRadioKeydown, rovingTabIndex } from '../radioKeys.js';
  import type { RadioCardOption } from '../types.js';
  import ChoiceCard from './ChoiceCard.svelte';

  interface Props {
    /** The group's accessible name: the question being asked ("How hard?"). */
    readonly label: string;
    /** The chosen option's value; `null` (since 1.7.0) while nothing is. */
    readonly value: T | null;
    readonly options: readonly RadioCardOption<T>[];
    readonly onchange: (value: T) => void;
    readonly testid?: string | undefined;
    readonly chosenTaglineTestid?: string | undefined;
    // ── 1.7.0 ──
    /** A second press on the chosen card clears it (`onclear`): toggle cards, not radios. */
    readonly deselectable?: boolean;
    /** The chosen card was pressed again, in a `deselectable` group. */
    readonly onclear?: (() => void) | undefined;
    // ── 1.8.0 ──
    /** The words a screen reader hears for a graded option; the shell's own by default. */
    readonly toneLabels?: { readonly right?: string; readonly wrong?: string } | undefined;
  }
  const {
    label,
    value,
    options,
    onchange,
    testid,
    chosenTaglineTestid,
    deselectable = false,
    onclear,
    toneLabels,
  }: Props = $props();

  let group = $state<HTMLDivElement | undefined>();
  const checkedIndex = $derived(options.findIndex((option) => option.value === value));

  function onKeydown(event: KeyboardEvent): void {
    handleRadioKeydown(
      event,
      group,
      checkedIndex,
      options.length,
      (index) => {
        const next = options[index];
        if (next !== undefined && next.value !== value && next.disabled !== true) {
          onchange(next.value);
        }
      },
      (index) => options[index]?.disabled === true,
    );
  }

  const isOff = (index: number): boolean => options[index]?.disabled === true;

  function press(option: RadioCardOption<T>): void {
    if (option.value !== value) onchange(option.value);
    else if (deselectable) onclear?.();
  }
</script>

<div
  class="ui-radio-cards"
  role={deselectable ? 'group' : 'radiogroup'}
  aria-label={label}
  data-testid={testid}
  bind:this={group}
>
  {#each options as option, index (option.value)}
    <ChoiceCard
      kind={deselectable ? 'toggle' : 'radio'}
      selected={value === option.value}
      name={option.name}
      tagline={option.tagline}
      detail={option.detail}
      extra={option.extra}
      meter={option.meter}
      tag={option.tag}
      lang={option.lang}
      avatar={option.avatar}
      richName={option.richName}
      tone={option.tone}
      detailLang={option.detailLang}
      toneLabel={option.tone === undefined ? undefined : toneLabels?.[option.tone]}
      disabled={option.disabled === true}
      testid={option.testid}
      tabindex={deselectable
        ? undefined
        : rovingTabIndex(index, checkedIndex, isOff, options.length)}
      taglineTestid={value === option.value ? chosenTaglineTestid : undefined}
      onclick={() => press(option)}
      onkeydown={deselectable ? undefined : onKeydown}
    />
  {/each}
</div>

<style>
  .ui-radio-cards {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-2);
  }
</style>
